import { createGameStore, type PersistedGameStore } from '@game-libs/generic-game-state';
import { registerStoreReset } from './index';
import { upgrades as defaultUpgradesBlueprint } from '../states/upgrades';
import type { EntityUpgrades, EntityUpgradesList, ResourceCost, Upgrade, UpgradeType } from '../../types/game-types';
import { UpgradeStatus } from '../../types/game-types';
import { applyBuyUpgradeIfAffordable } from '../reducers/buy-upgrade';

// ---------------------------------------------------------------------------
// Persist merge: tolerate partial JSON per entity / upgrade id.
// ---------------------------------------------------------------------------

function cloneDefaultUpgrades(): EntityUpgradesList {
  return structuredClone(defaultUpgradesBlueprint) as EntityUpgradesList;
}

function mergeCostRecord(persisted: unknown, template: ResourceCost): ResourceCost {
  if (!persisted || typeof persisted !== 'object' || Array.isArray(persisted)) {
    return { ...template };
  }
  const record = persisted as Record<string, unknown>;
  const next = { ...template } as Record<string, number | undefined>;
  for (const key of Object.keys(template) as (keyof ResourceCost)[]) {
    const value = record[key as string];
    if (typeof value === 'number' && Number.isFinite(value)) {
      next[key as string] = value;
    }
  }
  return next as ResourceCost;
}

function mergeUpgradeRecord(persisted: unknown, template: Upgrade): Upgrade {
  if (!persisted || typeof persisted !== 'object' || Array.isArray(persisted)) {
    return { ...template };
  }
  const record = persisted as Record<string, unknown>;
  const next: Upgrade = { ...template };

  if (typeof record.purchased === 'number' && Number.isFinite(record.purchased)) {
    next.purchased = record.purchased;
  }
  if (typeof record.multiplier === 'number' && Number.isFinite(record.multiplier)) {
    next.multiplier = record.multiplier;
  }
  if (typeof record.costGrowthRate === 'number' && Number.isFinite(record.costGrowthRate)) {
    next.costGrowthRate = record.costGrowthRate;
  }
  if (typeof record.bonusPerOwned === 'number' && Number.isFinite(record.bonusPerOwned)) {
    next.bonusPerOwned = record.bonusPerOwned;
  }
  if (typeof record.isUnlocked === 'boolean') {
    next.isUnlocked = record.isUnlocked;
  }
  if (record.status !== undefined && typeof record.status === 'string') {
    next.status = record.status as Upgrade['status'];
  }
  if (typeof record.purchaseLimit === 'number' && Number.isFinite(record.purchaseLimit)) {
    next.purchaseLimit = record.purchaseLimit;
  }
  if (record.cost !== undefined) {
    next.cost = mergeCostRecord(record.cost, template.cost);
  }
  if (Array.isArray(record.icon)) {
    next.icon = record.icon as string[];
  }

  return next;
}

function mergeEntityUpgrades(persisted: unknown, template: EntityUpgrades): EntityUpgrades {
  if (!persisted || typeof persisted !== 'object' || Array.isArray(persisted)) {
    return { ...template };
  }
  const incoming = persisted as Record<string, unknown>;
  const next: EntityUpgrades = { ...template };
  for (const upgradeKey of Object.keys(template)) {
    const raw = incoming[upgradeKey];
    if (raw !== undefined) {
      next[upgradeKey as UpgradeType] = mergeUpgradeRecord(raw, template[upgradeKey as UpgradeType]);
    }
  }
  return next;
}

function mergeUpgradesListFromSlice(persistedRoot: unknown, current: EntityUpgradesList): EntityUpgradesList {
  if (!persistedRoot || typeof persistedRoot !== 'object' || Array.isArray(persistedRoot)) {
    return current;
  }
  const record = persistedRoot as Record<string, unknown>;
  const rawUpgrades = record.upgrades !== undefined ? record.upgrades : persistedRoot;
  if (!rawUpgrades || typeof rawUpgrades !== 'object' || Array.isArray(rawUpgrades)) {
    return current;
  }
  const incoming = rawUpgrades as Record<string, unknown>;
  const blueprint = cloneDefaultUpgrades();
  const next: EntityUpgradesList = { ...blueprint };
  for (const entityKey of Object.keys(blueprint)) {
    const templateEntity = blueprint[entityKey];
    const merged = mergeEntityUpgrades(incoming[entityKey] ?? current[entityKey], templateEntity);
    next[entityKey] = merged;
  }
  return next;
}

function mergePersistedUpgradesRoot(persisted: unknown, current: UpgradesStore): UpgradesStore {
  if (!persisted || typeof persisted !== 'object') {
    return current;
  }
  return {
    ...current,
    upgrades: mergeUpgradesListFromSlice(persisted, current.upgrades),
  };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export type UpgradesState = {
  upgrades: EntityUpgradesList;
};

export type UpgradesActions = {
  setUpgrades: (next: EntityUpgradesList) => void;
  sync: (patch: Partial<UpgradesState>) => void;
  buyUpgrade: (upgradeId: UpgradeType, entityType: string) => boolean;
  /** Set listed upgrades to `Unlocked` (tree unlock). */
  unlockUpgradeChildren: (entityType: string, children: UpgradeType[]) => void;
  /** Show / lock UI state for listed upgrades. */
  showUpgradeChildren: (entityType: string, children: UpgradeType[]) => void;
  /** Cheat: every upgrade unlocked + visible. */
  unlockAllUpgrades: () => void;
  reset: () => void;
};

export type UpgradesStore = UpgradesState & UpgradesActions;

const INITIAL_UPGRADES_STATE: UpgradesState = {
  upgrades: cloneDefaultUpgrades(),
};

export const useUpgradesStore: PersistedGameStore<UpgradesStore> = createGameStore<UpgradesStore>(
  {
    saveKey: 'upgrades',
    persistConfig: {
      merge: (persisted, current) => mergePersistedUpgradesRoot(persisted, current as UpgradesStore),
    },
  },
  (set, get) => ({
    ...INITIAL_UPGRADES_STATE,

    setUpgrades: (next) => set({ upgrades: next }),

    sync: (patch) => set(patch as UpgradesStore),

    buyUpgrade: (upgradeId, entityType) => {
      const next = applyBuyUpgradeIfAffordable(get().upgrades, upgradeId, entityType);
      if (!next) {
        return false;
      }
      set({ upgrades: next });
      return true;
    },

    unlockUpgradeChildren: (entityType, children) => {
      const upgradesMap = get().upgrades;
      const unlockedUpgrades = {} as EntityUpgrades;
      for (const upgradeId of children) {
        unlockedUpgrades[upgradeId] = {
          ...upgradesMap[entityType][upgradeId],
          status: UpgradeStatus.Unlocked,
        };
      }
      set({
        upgrades: {
          ...upgradesMap,
          [entityType]: { ...upgradesMap[entityType], ...unlockedUpgrades },
        },
      });
    },

    showUpgradeChildren: (entityType, children) => {
      const upgradesMap = get().upgrades;
      const lockedUpgrades = {} as EntityUpgrades;
      for (const upgradeId of children) {
        let status = UpgradeStatus.Locked;
        if (upgradesMap[entityType][upgradeId].isLockedOnDemo) {
          status = UpgradeStatus.Unlocked;
        }
        lockedUpgrades[upgradeId] = {
          ...upgradesMap[entityType][upgradeId],
          status,
        };
      }
      set({
        upgrades: {
          ...upgradesMap,
          [entityType]: { ...upgradesMap[entityType], ...lockedUpgrades },
        },
      });
    },

    unlockAllUpgrades: () => {
      const next = structuredClone(get().upgrades) as EntityUpgradesList;
      for (const group of Object.values(next)) {
        for (const upgrade of Object.values(group)) {
          upgrade.isUnlocked = true;
          upgrade.status = UpgradeStatus.Unlocked;
        }
      }
      set({ upgrades: next });
    },

    reset: () => set({ upgrades: cloneDefaultUpgrades() }),
  }),
);

registerStoreReset(() => useUpgradesStore.getState().reset());

// ---------------------------------------------------------------------------
// Legacy main-blob migration (upgrades used to live on the monolithic save).
// ---------------------------------------------------------------------------

function upgradesMatchFactoryDefaults(current: EntityUpgradesList): boolean {
  try {
    return JSON.stringify(current) === JSON.stringify(cloneDefaultUpgrades());
  } catch {
    return false;
  }
}

/**
 * When the `upgrades` persist slice is still factory defaults but the main blob still has `upgrades`,
 * merge once. Skips if Zustand already hydrated real progress.
 */
export function applyLegacyUpgradesFromMainSaveIfStoreStillDefault(legacyUpgrades: unknown): void {
  if (legacyUpgrades === undefined || legacyUpgrades === null) {
    return;
  }
  if (!upgradesMatchFactoryDefaults(useUpgradesStore.getState().upgrades)) {
    return;
  }
  const merged = mergeUpgradesListFromSlice({ upgrades: legacyUpgrades }, cloneDefaultUpgrades());
  useUpgradesStore.getState().setUpgrades(merged);
}
